import { fetchTab } from "@/api/hooks/tab";
import { createCountryForecastPlot } from "@/api/hooks/tools/countryForecastPlot";
import type { PlotSeriesType } from "@/reducer/client-models";
import type { CountryPlotType, CountryPlotTypeUpdate } from "@/reducer/client-models/CountryPlot";
import Logger from "@mm/logging";
import type { CountryPlotSchema, ElementStyle } from "@mm/metx-workbench.meteomatics.com";
import { type PayloadAction, createSelector, createSlice } from "@reduxjs/toolkit";
import { DateTime, Duration } from "luxon";
import { useSelector } from "react-redux";
import type { RootState } from ".";

const logger = Logger.fromFilename(__filename);

export type CountryPlotState = {
  [id: number]: CountryPlotType;
};

const initialState: CountryPlotState = {};

const countryPlotSlice = createSlice({
  name: "countryPlot",
  initialState: initialState as CountryPlotState,
  reducers: {
    addCountryPlot(state, action: PayloadAction<CountryPlotType>) {
      state[action.payload.id] = action.payload;
    },
    discardCountryPlot(state, action: PayloadAction<{ id: number }>) {
      const { id } = action.payload;
      const tool = state[id];

      if (!tool) {
        logger.error("Could not find tool with id", id);
        return;
      }
      delete state[id];
    },
    setCountryPlotProps(state, action: PayloadAction<{ id: number; props: Partial<CountryPlotSchema> }>) {
      const { id, props } = action.payload;
      const countryPlot = state[id];

      if (!countryPlot) {
        logger.error("Could not find plot with id", id);
        return;
      }

      Object.assign(countryPlot, props);
    },
    setCountryPlotSerieVisibility(state, action: PayloadAction<{ id: number; props: { is10y: boolean } }>) {
      const { id, props } = action.payload;
      const countryPlot = state[id];

      if (!countryPlot) {
        logger.error("Could not find plot with id", id);
        return;
      }
      const serieIndex = props.is10y ? 0 : 1;
      state[id].plotSeries[serieIndex].visible = !state[id].plotSeries[serieIndex].visible;
    },
    setCountryPlotTitle(state, action: PayloadAction<{ id: number; title: string }>) {
      const { id, title } = action.payload;

      const countryPlot = state[id];
      if (!countryPlot) {
        logger.error("Could not find countryPlot with id", id);
        return;
      }

      countryPlot.title = title;
    },
    setCountryPlotTitleStyle(state, action: PayloadAction<{ style: ElementStyle; id: number }>) {
      const { style, id } = action.payload;

      const countryPlot = state[id];

      if (!countryPlot) {
        logger.error("Could not find countryPlot with id", id);
        return;
      }

      countryPlot.titleStyle = style;
    },
    updateCountryPlotState(
      state,
      action: PayloadAction<{
        id: CountryPlotSchema["id"];
        countryPlot: CountryPlotTypeUpdate;
      }>,
    ) {
      const { countryPlot, id } = action.payload;

      const countryPlotFromState = state[id];

      if (!countryPlotFromState) {
        logger.error("Could not find countryPlot with id", id);
        return;
      }

      const updatedCountryPlot = {
        ...countryPlotFromState,
        ...countryPlot,
      };

      state[id] = updatedCountryPlot;
    },
    setCountryPlotTimeRange(
      state,
      action: PayloadAction<{
        id: number;
        props: { datetimeRange: [string | null, string | null]; resolution: string | null; referenceTime: string };
      }>,
    ) {
      const {
        id,
        props: {
          datetimeRange: [from, to],
          resolution,
          referenceTime,
        },
      } = action.payload;

      const countryPlot = state[id];

      if (!countryPlot) {
        logger.error("Could not find plot with id", id);
        return;
      }
      countryPlot.resolution = resolution;
      if (!countryPlot.time_lock) {
        countryPlot.datetime_from = null;
        countryPlot.datetime_to = null;

        countryPlot.datetime_from_offset = from
          ? DateTime.fromISO(from).diff(DateTime.fromISO(referenceTime)).toISO()
          : null;
        countryPlot.datetime_to_offset = to ? DateTime.fromISO(to).diff(DateTime.fromISO(referenceTime)).toISO() : null;

        countryPlot.resolution = resolution;
        return;
      }

      countryPlot.datetime_from = from;
      countryPlot.datetime_to = to;
    },
    setPlotTimeSettings(
      state,
      action: PayloadAction<{ id: number; timeLock: boolean; from: string; to: string; referenceTime: string }>,
    ) {
      const { id, timeLock, from, to, referenceTime } = action.payload;

      const countryPlot = state[id];

      if (!countryPlot) {
        logger.error("Could not find countryPlot with id", id);
        return;
      }

      countryPlot.time_lock = timeLock;

      if (!timeLock) {
        countryPlot.datetime_from = null;
        countryPlot.datetime_from_offset = from
          ? DateTime.fromISO(from).diff(DateTime.fromISO(referenceTime)).toISO()
          : null;

        countryPlot.datetime_to = null;
        countryPlot.datetime_to_offset = to ? DateTime.fromISO(to).diff(DateTime.fromISO(referenceTime)).toISO() : null;

        return;
      }

      countryPlot.datetime_from = countryPlot.datetime_from_offset
        ? DateTime.fromISO(referenceTime).plus(Duration.fromISO(countryPlot.datetime_from_offset)).toISO()
        : DateTime.fromISO(from).toISO();
      countryPlot.datetime_from_offset = null;

      countryPlot.datetime_to = countryPlot.datetime_to_offset
        ? DateTime.fromISO(referenceTime).plus(Duration.fromISO(countryPlot.datetime_to_offset)).toISO()
        : DateTime.fromISO(to).toISO();
      countryPlot.datetime_to_offset = null;

      countryPlot.resolution = "PT1H";
    },
    adjustAllCountryPlotDateTimeRange(state, action: PayloadAction<{ durationMinutes: number }>) {
      const { durationMinutes } = action.payload;
      for (const plotId in state) {
        const currentPlot = state[plotId];
        const { datetime_from, datetime_to, time_lock } = currentPlot;

        if (time_lock) {
          continue;
        }

        if (datetime_from) {
          const newFrom = DateTime.fromISO(datetime_from).plus({ minute: durationMinutes });
          const newTo = datetime_to ? DateTime.fromISO(datetime_to).plus({ minute: durationMinutes }) : null;
          currentPlot.datetime_from = newFrom.toISO();
          currentPlot.datetime_to = newTo?.toISO() || null;
        }
      }
    },
  },
  extraReducers: (builder) => {
    builder.addCase(fetchTab.fulfilled, (state, action) => {
      const tab = action.payload;
      for (const key in state) {
        delete state[+key];
      }
      for (const countryPlot of tab.country_plots) {
        state[countryPlot.id] = countryPlot as CountryPlotType;
      }
    });
    builder.addCase(createCountryForecastPlot.fulfilled, (state, action) => {
      const countryPlot = action.payload.tool;
      state[countryPlot.id] = countryPlot;
    });
  },
});

const selectCountryPlots = (state: RootState) => state.tabGroup.present.countryPlots;

const makeSelectCountryPlotsByTabId = createSelector(
  [selectCountryPlots, (state: RootState, tabId: number) => tabId],
  (countryPlots, tabId) => Object.values(countryPlots).filter((plot) => plot.id_tab === tabId),
);

export function useCountryPlots(tabId: number) {
  return useSelector((state: RootState) => makeSelectCountryPlotsByTabId(state, tabId));
}

const makeSelectCountryPlotById = createSelector(
  [selectCountryPlots, (state: RootState, id: number) => id],
  (countryPlots, id) => countryPlots[id],
);

export function useCountryPlot(id: number) {
  return useSelector((state: RootState) => makeSelectCountryPlotById(state, id));
}

export const {
  addCountryPlot,
  discardCountryPlot,
  setCountryPlotProps,
  setCountryPlotTitle,
  setCountryPlotTitleStyle,
  setCountryPlotSerieVisibility,
  setCountryPlotTimeRange,
  setPlotTimeSettings,
  updateCountryPlotState,
  adjustAllCountryPlotDateTimeRange,
} = countryPlotSlice.actions;

export default countryPlotSlice.reducer;
